import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LoadingSpinner from '../ui/LoadingSpinner';
import EmptyState from '../ui/EmptyState';
import ErrorMessage from '../ui/ErrorMessage';
import HorizontalTableShell from '../ui/HorizontalTableShell';
import LineChart from '../charts/LineChart';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartColors, getChartJsOptions } from '../../utils/themeHelpers';
import { calculateLinearRegressionLine } from '../../utils/chartRegression';
import {
  BranchSummaryItem,
  BranchMetricTrendPoint,
  BranchMetricTrendSeries,
  useBranchSummaryData,
  useBrandBranchMetricTrend
} from '../../hooks/useBrandQueries';
import {
  BRANCH_COMPARISON_METRICS,
  formatMetricValue,
  getMetricValue,
  getNumber,
  MetricDefinition
} from '../../utils/brandComparisonMetrics';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

interface BrandOverviewBranchComparisonTableProps {
  brandId?: number | null;
  timeRange?: string;
  loading?: boolean;
}

type ComparisonStatus = 'better' | 'equal' | 'worse';

interface BranchTableRow {
  branchId: number;
  branchName: string;
  currency: string;
  rowType: 'brand' | 'branch';
  rowNumber: number | null;
  originalBranchOrder: number;
  metrics: Record<string, number>;
}

type SortDirection = 'asc' | 'desc';
type ChartDisplayMode = 'actual' | 'trend';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DEFAULT_SELECTED_METRICS = BRANCH_COMPARISON_METRICS.map(metric => metric.id);

const TOLERANCE_PERCENT = 2;

const formatChartMetricValue = (
  value: number,
  metric: MetricDefinition | null,
  currency = 'تومان',
  withUnit = true
): string => {
  if (!metric) {
    return value.toLocaleString('fa-IR');
  }

  switch (metric.unit) {
    case 'currency': {
      const formatted = Math.round(value).toLocaleString('fa-IR');
      return withUnit ? `${formatted} ${currency}` : formatted;
    }
    case 'percentage': {
      const formatted = value.toLocaleString('fa-IR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
      return withUnit ? `${formatted}%` : formatted;
    }
    default:
      return Math.round(value).toLocaleString('fa-IR');
  }
};

const getStatus = (diffPercent: number | null): ComparisonStatus => {
  if (diffPercent === null) return 'equal';
  if (diffPercent > TOLERANCE_PERCENT) return 'better';
  if (diffPercent < -TOLERANCE_PERCENT) return 'worse';
  return 'equal';
};

const formatDiff = (value: number | null): string => {
  if (value === null || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString('fa-IR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })}%`;
};

const statusVisual = {
  better: {
    container: 'bg-status-success-bg text-status-success',
    icon: 'text-status-success',
    iconChar: '▲'
  },
  equal: {
    container: 'bg-bg-tertiary text-text-secondary',
    icon: 'text-text-tertiary',
    iconChar: '—'
  },
  worse: {
    container: 'bg-status-error-bg text-status-error',
    icon: 'text-status-error',
    iconChar: '▼'
  }
};

const getSeriesPoints = (series: BranchMetricTrendSeries): BranchMetricTrendPoint[] => {
  if (!Array.isArray(series?.points)) return [];
  return series.points;
};

const BrandOverviewBranchComparisonTable: React.FC<BrandOverviewBranchComparisonTableProps> = ({
  brandId = null,
  timeRange = 'last6Months',
  loading = false
}) => {
  const { currentTheme } = useTheme();
  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary
  } = useBranchSummaryData(brandId, timeRange);
  const branches = summaryData?.branches ?? [];
  const brandAverage = summaryData?.brandAverage ?? null;
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(DEFAULT_SELECTED_METRICS);
  const [showMetricPicker, setShowMetricPicker] = useState<boolean>(false);
  const [sortColumn, setSortColumn] = useState<string>('rowNumber');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [activeChartMetric, setActiveChartMetric] = useState<string | null>(null);
  const [chartDisplayMode, setChartDisplayMode] = useState<ChartDisplayMode>('actual');
  const chartSectionRef = useRef<HTMLDivElement>(null);

  const {
    data: branchTrendData,
    isLoading: trendLoading,
    error: trendError,
    refetch: refetchTrend
  } = useBrandBranchMetricTrend(brandId, activeChartMetric, timeRange);

  const effectiveMetrics = useMemo(
    () => BRANCH_COMPARISON_METRICS.filter(metric => selectedMetrics.includes(metric.id)),
    [selectedMetrics]
  );

  const activeMetricDefinition = useMemo(
    () => BRANCH_COMPARISON_METRICS.find(metric => metric.id === activeChartMetric) ?? null,
    [activeChartMetric]
  );

  const hasActiveMetric = useMemo(
    () => !!activeChartMetric && selectedMetrics.includes(activeChartMetric),
    [activeChartMetric, selectedMetrics]
  );

  const effectiveBrandAverage = useMemo<BranchSummaryItem>(() => {
    if (brandAverage) return brandAverage;

    return {
      branchId: 0,
      branchName: 'برند',
      currency: branches[0]?.currency || 'تومان',
      currentMonthRevenue: 0,
      customerCount: 0,
      avgTransactionValue: 0,
      invoiceCount: 0,
      subscription: 0,
      creditInvoiceCount: 0,
      creditInvoiceAmount: 0,
      usedCredit: 0,
      satisfactionScore: 0,
      clubMembershipRate: 0,
      performanceScore: 0,
      newCustomers: 0,
      returningCustomers: 0,
      lostCustomers: 0
    };
  }, [brandAverage, branches]);

  const effectiveMetricAverages = useMemo(
    () => ({
      revenue: getMetricValue(effectiveBrandAverage, 'revenue'),
      invoiceCount: getMetricValue(effectiveBrandAverage, 'invoiceCount'),
      customerCount: getMetricValue(effectiveBrandAverage, 'customerCount'),
      satisfactionScore: getMetricValue(effectiveBrandAverage, 'satisfactionScore'),
      subscription: getMetricValue(effectiveBrandAverage, 'subscription'),
      creditInvoiceCount: getMetricValue(effectiveBrandAverage, 'creditInvoiceCount'),
      creditInvoiceAmount: getMetricValue(effectiveBrandAverage, 'creditInvoiceAmount'),
      usedCredit: getMetricValue(effectiveBrandAverage, 'usedCredit'),
      newCustomers: getNumber(effectiveBrandAverage.newCustomers),
      returningCustomers: getNumber(effectiveBrandAverage.returningCustomers),
      lostCustomers: getNumber(effectiveBrandAverage.lostCustomers)
    }),
    [effectiveBrandAverage]
  );

  const rawTableData = useMemo<BranchTableRow[]>(() => {
    const branchRows: BranchTableRow[] = branches.map((branch, index) => {
      const rowMetrics: Record<string, number> = {};
      BRANCH_COMPARISON_METRICS.forEach(metric => {
        rowMetrics[metric.id] = getMetricValue(branch, metric.id);
      });

      return {
        branchId: branch.branchId,
        branchName: branch.branchName,
        currency: branch.currency,
        rowType: 'branch',
        rowNumber: index + 1,
        originalBranchOrder: index + 1,
        metrics: rowMetrics
      };
    });

    if (branchRows.length === 0) {
      return [];
    }

    const brandRowMetrics: Record<string, number> = {};
    BRANCH_COMPARISON_METRICS.forEach(metric => {
      brandRowMetrics[metric.id] = getNumber(effectiveMetricAverages[metric.id]);
    });

    const brandRow: BranchTableRow = {
      branchId: 0,
      branchName: effectiveBrandAverage.branchName || 'برند',
      currency: effectiveBrandAverage.currency || branchRows[0].currency || 'تومان',
      rowType: 'brand',
      rowNumber: null,
      originalBranchOrder: 0,
      metrics: brandRowMetrics
    };

    return [brandRow, ...branchRows];
  }, [branches, effectiveBrandAverage, effectiveMetricAverages]);

  const tableData = useMemo<BranchTableRow[]>(() => {
    if (!rawTableData.length) {
      return [];
    }

    const brandRow = rawTableData.find(row => row.rowType === 'brand') ?? null;
    const branchRows = rawTableData.filter(row => row.rowType === 'branch');
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

    const sortedBranchRows = [...branchRows].sort((a, b) => {
      if (sortColumn === 'branchName') {
        const nameComparison = a.branchName.localeCompare(b.branchName, 'fa');
        if (nameComparison !== 0) {
          return nameComparison * directionMultiplier;
        }
        return a.originalBranchOrder - b.originalBranchOrder;
      }

      const aValue = sortColumn === 'rowNumber' ? a.originalBranchOrder : (a.metrics[sortColumn] ?? 0);
      const bValue = sortColumn === 'rowNumber' ? b.originalBranchOrder : (b.metrics[sortColumn] ?? 0);
      if (aValue === bValue) {
        return a.originalBranchOrder - b.originalBranchOrder;
      }
      return (aValue - bValue) * directionMultiplier;
    });

    const numberedBranchRows = sortedBranchRows.map((row, index) => ({
      ...row,
      rowNumber: index + 1
    }));

    if (brandRow) {
      return [{ ...brandRow, rowNumber: null }, ...numberedBranchRows];
    }

    return numberedBranchRows;
  }, [rawTableData, sortColumn, sortDirection]);

  useEffect(() => {
    if (activeChartMetric && !selectedMetrics.includes(activeChartMetric)) {
      setActiveChartMetric(null);
    }
  }, [activeChartMetric, selectedMetrics]);

  const handleChartButtonClick = useCallback((metricId: string) => {
    setActiveChartMetric(prev => {
      const next = prev === metricId ? null : metricId;
      if (next !== null) {
        setTimeout(() => {
          chartSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
      }
      return next;
    });
  }, []);

  const handleColumnSort = useCallback((columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(columnKey);
    setSortDirection('asc');
  }, [sortColumn]);

  const handleCloseChart = useCallback(() => {
    setActiveChartMetric(null);
  }, []);

  const toggleMetric = (metricId: string) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(id => id !== metricId);
      }
      return [...prev, metricId];
    });
  };

  const selectAllMetrics = () => {
    setSelectedMetrics(BRANCH_COMPARISON_METRICS.map(metric => metric.id));
  };

  const clearAllMetrics = () => {
    setSelectedMetrics([]);
  };

  const chartColors = useMemo(() => getChartColors(currentTheme), [currentTheme]);

  const chartCurrency = useMemo(() => {
    if (activeMetricDefinition?.unit !== 'currency') return 'تومان';
    return branchTrendData?.currency || branches[0]?.currency || 'تومان';
  }, [activeMetricDefinition?.unit, branchTrendData?.currency, branches]);

  const chartLabels = useMemo(() => {
    if (branchTrendData?.labels && branchTrendData.labels.length > 0) {
      return branchTrendData.labels;
    }
    if (!branchTrendData?.brandAverage) return [];
    return getSeriesPoints(branchTrendData.brandAverage).map(point => point.month);
  }, [branchTrendData]);

  const chartDatasets = useMemo(() => {
    if (!branchTrendData) {
      return [];
    }

    const branchDatasets = branchTrendData.branches.map((series, index) => {
      const points = getSeriesPoints(series);
      const actualValues = points.map(point => getNumber(point.value));
      const trendValues = calculateLinearRegressionLine(actualValues);

      return {
        label: series.branchName,
        data: chartDisplayMode === 'trend' ? trendValues : actualValues,
        borderColor: chartColors[index % chartColors.length],
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: chartDisplayMode === 'trend' ? 0 : 0.35,
        pointRadius: chartDisplayMode === 'trend' ? 0 : 3,
        pointHoverRadius: chartDisplayMode === 'trend' ? 0 : 5,
        pointBorderWidth: 2,
        pointBackgroundColor: '#ffffff',
        fill: false
      };
    });

    const brandSeriesPoints = getSeriesPoints(branchTrendData.brandAverage);
    const brandActualValues = brandSeriesPoints.map(point => getNumber(point.value));
    const brandTrendValues = calculateLinearRegressionLine(brandActualValues);

    const brandDataset = {
      label: 'میانگین برند',
      data: chartDisplayMode === 'trend' ? brandTrendValues : brandActualValues,
      borderColor: currentTheme === 'dark' ? '#f8fafc' : '#111827',
      backgroundColor: 'transparent',
      borderWidth: 3,
      borderDash: [6, 4],
      tension: chartDisplayMode === 'trend' ? 0 : 0.35,
      pointRadius: chartDisplayMode === 'trend' ? 0 : 4,
      pointHoverRadius: chartDisplayMode === 'trend' ? 0 : 6,
      pointBorderWidth: 2,
      pointBackgroundColor: '#ffffff',
      fill: false
    };

    return [...branchDatasets, brandDataset];
  }, [branchTrendData, chartColors, chartDisplayMode, currentTheme]);

  const trendChartData = useMemo(() => ({
    labels: chartLabels,
    datasets: chartDatasets
  }), [chartDatasets, chartLabels]);

  const trendChartOptions = useMemo(() => {
    const unitForAxis = activeMetricDefinition?.unit === 'currency'
      ? chartCurrency
      : activeMetricDefinition?.unit === 'percentage'
        ? '%'
        : undefined;

    const baseOptions = getChartJsOptions(currentTheme, unitForAxis);

    return {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          ...baseOptions.plugins?.tooltip,
          callbacks: {
            label: (context: any) => {
              const value = Number(context.parsed?.y ?? 0);
              const formattedValue = formatChartMetricValue(
                value,
                activeMetricDefinition,
                chartCurrency
              );
              return `${context.dataset.label}: ${formattedValue}`;
            }
          }
        }
      },
      scales: {
        ...baseOptions.scales,
        x: {
          ...baseOptions.scales?.x,
          ticks: {
            ...baseOptions.scales?.x?.ticks,
            autoSkip: true,
            maxRotation: 0
          }
        },
        y: {
          ...baseOptions.scales?.y,
          ticks: {
            ...baseOptions.scales?.y?.ticks,
            callback: (value: any) => (
              formatChartMetricValue(
                Number(value),
                activeMetricDefinition,
                chartCurrency,
                false
              )
            )
          }
        }
      }
    };
  }, [activeMetricDefinition, chartCurrency, currentTheme]);

  if (loading || summaryLoading) {
    return (
      <div className="card-base card-padding">
        <h3 className="title-card mb-4">گزارش مقایسه شعب</h3>
        <div className="flex flex-col items-center justify-center py-8">
          <LoadingSpinner size="md" variant="minimal" message="در حال بارگذاری گزارش مقایسه…" />
        </div>
      </div>
    );
  }

  if (summaryError) {
    return (
      <div className="card-base card-padding">
        <h3 className="title-card mb-4">گزارش مقایسه شعب</h3>
        <ErrorMessage
          message="خطا در بارگذاری گزارش مقایسه شعب"
          onRetry={() => refetchSummary()}
          className="m-0"
        />
      </div>
    );
  }

  if (!brandId) {
    return (
      <div className="card-base card-padding">
        <h3 className="title-card mb-4">گزارش مقایسه شعب</h3>
        <EmptyState message="برندی برای نمایش انتخاب نشده است" icon="🏷️" />
      </div>
    );
  }

  if (!branches || branches.length === 0) {
    return (
      <div className="card-base card-padding">
        <h3 className="title-card mb-4">گزارش مقایسه شعب</h3>
        <EmptyState message="داده‌ای برای مقایسه شعب وجود ندارد" icon="🏬" />
      </div>
    );
  }

  return (
    <div className="card-base card-padding space-y-8 min-w-0">
      <h3 id="brand-branch-comparison-heading" className="title-card text-lg md:text-xl">
        مقایسه شعب با میانگین برند
      </h3>

      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:gap-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm md:text-base font-semibold text-text-primary">معیارهای نمایش</div>
            <button
              type="button"
              className="text-sm md:text-base text-primary-600 hover:text-primary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 rounded px-3 py-2 min-h-[40px]"
              onClick={() => setShowMetricPicker(prev => !prev)}
              aria-expanded={showMetricPicker}
              aria-controls="metric-picker-panel"
            >
              {/* {showMetricPicker ? 'بستن تنظیمات معیارها' : 'نمایش پنجره انتخاب معیارها'} */}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {BRANCH_COMPARISON_METRICS.map((metric) => {
              const isSelected = selectedMetrics.includes(metric.id);
              return (
                <button
                  key={metric.id}
                  type="button"
                  onClick={() => toggleMetric(metric.id)}
                  className={`rounded-full border px-3 py-1.5 text-xs md:text-sm transition-colors min-h-[36px] ${
                    isSelected
                      ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                      : 'bg-bg-secondary text-text-secondary border-border-default hover:text-text-primary'
                  }`}
                  title={metric.label}
                >
                  {metric.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 text-xs md:text-sm">
            <button
              type="button"
              onClick={selectAllMetrics}
              className="text-primary-600 hover:text-primary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 rounded px-2 py-1"
            >
              انتخاب همه
            </button>
            <span className="text-text-tertiary" aria-hidden="true">|</span>
            <button
              type="button"
              onClick={clearAllMetrics}
              className="text-text-secondary hover:text-text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 rounded px-2 py-1"
            >
              پاک کردن همه
            </button>
          </div>
        </div>

        {/* {showMetricPicker && (
          <div
            id="metric-picker-panel"
            className="overflow-hidden rounded-md border border-border-default"
          >
            <MetricMultiSelector
              metrics={BRANCH_COMPARISON_METRICS}
              selected={selectedMetrics}
              onChange={setSelectedMetrics}
            />
          </div>
        )} */}
      </div>

      {effectiveMetrics.length === 0 ? (
        <div className="text-sm text-text-secondary p-3 bg-bg-tertiary rounded-md">
          حداقل یک معیار برای نمایش جدول انتخاب کنید.
        </div>
      ) : (
        <>
          <HorizontalTableShell
            minWidth={180 + effectiveMetrics.length * 160}
            panelClassName=""
            scrollHint="برای مشاهده ستون‌های بیشتر، جدول را به چپ و راست بکشید"
          >
                <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
                  <thead>
                    <tr className="border-b-2 !bg-gray-100 dark:!bg-gray-800" style={{ borderColor: 'var(--border-secondary)' }}>
                      {/* ردیف */}
                      <th
                        className="px-3 py-4 text-center text-xs font-bold border-l last:border-l-0"
                        style={{ color: 'var(--text-primary)', borderColor: 'var(--border-primary)', minWidth: '60px' }}
                      >
                        <div className="flex flex-col items-center justify-center gap-2 min-h-[3rem]">
                          <span className="font-semibold text-sm">ردیف</span>
                          <button
                            onClick={() => handleColumnSort('rowNumber')}
                            className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                            style={{
                              backgroundColor: sortColumn === 'rowNumber' ? 'rgba(var(--accent-cyan-rgb, 6, 182, 212), 0.2)' : 'var(--bg-tertiary)',
                              color: sortColumn === 'rowNumber' ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                              border: '1px solid',
                              borderColor: sortColumn === 'rowNumber' ? 'var(--accent-cyan)' : 'var(--border-primary)'
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {sortColumn === 'rowNumber' && sortDirection === 'desc'
                                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                              }
                            </svg>
                            <span className="text-[10px] font-medium">
                              {sortColumn === 'rowNumber' ? (sortDirection === 'asc' ? 'صعودی' : 'نزولی') : 'مرتب'}
                            </span>
                          </button>
                        </div>
                      </th>
                      {/* نام شعبه */}
                      <th
                        className="px-3 py-4 text-center text-xs font-bold border-l last:border-l-0"
                        style={{ color: 'var(--text-primary)', borderColor: 'var(--border-primary)', minWidth: '120px' }}
                      >
                        <div className="flex flex-col items-center justify-center gap-2 min-h-[3rem]">
                          <span className="font-semibold text-sm">نام شعبه</span>
                          <button
                            onClick={() => handleColumnSort('branchName')}
                            className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                            style={{
                              backgroundColor: sortColumn === 'branchName' ? 'rgba(var(--accent-cyan-rgb, 6, 182, 212), 0.2)' : 'var(--bg-tertiary)',
                              color: sortColumn === 'branchName' ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                              border: '1px solid',
                              borderColor: sortColumn === 'branchName' ? 'var(--accent-cyan)' : 'var(--border-primary)'
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {sortColumn === 'branchName' && sortDirection === 'desc'
                                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                              }
                            </svg>
                            <span className="text-[10px] font-medium">
                              {sortColumn === 'branchName' ? (sortDirection === 'asc' ? 'صعودی' : 'نزولی') : 'مرتب'}
                            </span>
                          </button>
                        </div>
                      </th>
                      {/* Metric Columns */}
                      {effectiveMetrics.map(metric => {
                        const isChartActive = activeChartMetric === metric.id;
                        return (
                          <th
                            key={metric.id}
                            className="px-3 py-4 text-center text-xs font-bold border-l last:border-l-0"
                            style={{ color: 'var(--text-primary)', borderColor: 'var(--border-primary)', minWidth: '150px' }}
                          >
                            <div className="flex flex-col items-center justify-center gap-2 min-h-[3rem]">
                              <span className="font-semibold text-sm whitespace-nowrap">{metric.label}</span>
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleColumnSort(metric.id)}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                                  style={{
                                    backgroundColor: sortColumn === metric.id ? 'rgba(var(--accent-cyan-rgb, 6, 182, 212), 0.2)' : 'var(--bg-tertiary)',
                                    color: sortColumn === metric.id ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                                    border: '1px solid',
                                    borderColor: sortColumn === metric.id ? 'var(--accent-cyan)' : 'var(--border-primary)'
                                  }}
                                  title="مرتب‌سازی"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {sortColumn === metric.id && sortDirection === 'desc'
                                      ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                                      : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                                    }
                                  </svg>
                                  <span className="text-[10px] font-medium">
                                    {sortColumn === metric.id ? (sortDirection === 'asc' ? 'صعودی' : 'نزولی') : 'مرتب'}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleChartButtonClick(metric.id)}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                                  style={{
                                    backgroundColor: isChartActive ? 'rgba(var(--accent-cyan-rgb, 6, 182, 212), 0.2)' : 'var(--bg-tertiary)',
                                    color: isChartActive ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                                    border: '1px solid',
                                    borderColor: isChartActive ? 'var(--accent-cyan)' : 'var(--border-primary)'
                                  }}
                                  title={isChartActive ? `بستن نمودار ${metric.label}` : `نمایش نمودار ${metric.label}`}
                                >
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                  </svg>
                                  <span className="text-[10px] font-medium">نمودار</span>
                                </button>
                              </div>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row, rowIndex) => {
                      const isBrandRow = row.rowType === 'brand';
                      return (
                        <tr
                          key={`${row.branchId}-${rowIndex}`}
                          className={`group transition-all border-b ${
                            isBrandRow
                              ? '!bg-primary-50 dark:!bg-primary-900/20 font-bold border-y-2'
                              : rowIndex % 2 === 0
                                ? '!bg-white dark:!bg-gray-900'
                                : '!bg-gray-50 dark:!bg-gray-800'
                          } hover:!bg-blue-50 dark:hover:!bg-gray-700`}
                          style={{ borderColor: 'var(--border-primary)' }}
                        >
                          {/* ردیف */}
                          <td
                            className="px-3 py-4 text-center text-sm font-bold border-l last:border-l-0"
                            style={{ borderColor: 'var(--border-primary)', color: isBrandRow ? 'var(--primary-700)' : 'var(--text-primary)' }}
                          >
                            {isBrandRow ? '—' : Number(row.rowNumber ?? 0).toLocaleString('fa-IR')}
                          </td>
                          {/* نام شعبه */}
                          <td
                            className="px-3 py-4 text-center text-sm border-l last:border-l-0"
                            style={{ borderColor: 'var(--border-primary)', color: isBrandRow ? 'var(--primary-700)' : 'var(--text-primary)' }}
                          >
                            <span className={isBrandRow ? 'font-extrabold' : 'font-semibold'} title={row.branchName}>
                              {row.branchName}
                            </span>
                          </td>
                          {/* Metric cells */}
                          {effectiveMetrics.map(metric => {
                            const metricData = row.metrics[metric.id];
                            const average = effectiveMetricAverages[metric.id];
                            const hasAverage = typeof average === 'number' && Number.isFinite(average) && average !== 0;
                            const diffPercent = !isBrandRow && hasAverage ? ((metricData - average) / average) * 100 : null;
                            const status = getStatus(diffPercent);
                            const visual = statusVisual[status];
                            return (
                              <td
                                key={metric.id}
                                className={`px-3 py-4 text-center text-sm border-l last:border-l-0 transition-colors ${
                                  isBrandRow ? 'bg-primary-50 text-primary-700' : statusVisual[status].container
                                }`}
                                style={{ borderColor: 'var(--border-primary)' }}
                              >
                                <div className="flex flex-col items-center">
                                  <span className={`font-semibold ${isBrandRow ? 'font-bold' : ''}`}>
                                    {formatMetricValue(metricData, metric.unit, row.currency)}
                                  </span>
                                  {!isBrandRow && (
                                    <div
                                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold leading-none mt-1"
                                      style={{
                                        backgroundColor: status === 'better'
                                          ? 'rgba(var(--status-success-rgb, 5, 150, 105), 0.1)'
                                          : status === 'worse'
                                            ? 'rgba(var(--status-error-rgb, 220, 38, 38), 0.1)'
                                            : 'var(--bg-tertiary)',
                                        color: status === 'better'
                                          ? 'var(--status-success)'
                                          : status === 'worse'
                                            ? 'var(--status-error)'
                                            : 'var(--text-secondary)'
                                      }}
                                      title={`اختلاف با میانگین برند: ${formatDiff(diffPercent)}`}
                                    >
                                      <span className="text-xs">
                                        {status === 'better' ? '↗' : status === 'worse' ? '↘' : '—'}
                                      </span>
                                      <span>{formatDiff(diffPercent)}</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
          </HorizontalTableShell>

          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 text-xs md:text-sm" role="note" aria-label="راهنمای وضعیت مقایسه">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-status-success" aria-hidden="true"></div>
              <span className="text-text-secondary">بالاتر از میانگین برند</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-bg-tertiary" aria-hidden="true"></div>
              <span className="text-text-secondary">نزدیک به میانگین برند</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-status-error" aria-hidden="true"></div>
              <span className="text-text-secondary">پایین‌تر از میانگین برند</span>
            </div>
          </div>

          
        </>


      )}

      {hasActiveMetric && (
        <div ref={chartSectionRef} className="card-base card-padding space-y-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 rounded-full bg-primary-500"></div>
              <span className="title-card text-sm md:text-base">
                نمودار روند {activeMetricDefinition?.label}
              </span>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-2">
              <button
                type="button"
                onClick={handleCloseChart}
                className="btn btn-ghost inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-bg-secondary hover:bg-bg-tertiary transition-all text-xs md:text-sm"
                aria-label="بستن نمودار روند"
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="hidden lg:inline">بستن نمودار</span>
              </button>
              <div className="flex items-center gap-2">
                {/* <button
                  type="button"
                  onClick={() => setChartDisplayMode('actual')}
                  className={`rounded-md px-3 py-1.5 text-xs md:text-sm transition-colors ${
                    chartDisplayMode === 'actual'
                      ? 'bg-primary-600 text-white'
                      : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
                  }`}
                  aria-pressed={chartDisplayMode === 'actual'}
                >
                  مقدار واقعی
                </button> */}
                {/* <button
                  type="button"
                  onClick={() => setChartDisplayMode('trend')}
                  className={`rounded-md px-3 py-1.5 text-xs md:text-sm transition-colors ${
                    chartDisplayMode === 'trend'
                      ? 'bg-primary-600 text-white'
                      : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
                  }`}
                  aria-pressed={chartDisplayMode === 'trend'}
                >
                  خط روند
                </button> */}
              </div>
            </div>
          </div>

          {trendLoading ? (
            <div className="py-6">
              <LoadingSpinner size="sm" variant="minimal" message="در حال بارگذاری نمودار…" />
            </div>
          ) : trendError ? (
            <ErrorMessage
              message="خطا در بارگذاری نمودار روند"
              onRetry={() => refetchTrend()}
              className="m-0"
            />
          ) : chartLabels.length === 0 || chartDatasets.length === 0 ? (
            <div className="text-sm text-text-secondary p-3 bg-bg-tertiary rounded-md">
              داده‌ای برای نمایش نمودار این معیار وجود ندارد.
            </div>
          ) : (
            <>
              <div className="relative w-full min-w-0" style={{ height: 360 }}>
                <LineChart
                  data={trendChartData}
                  options={trendChartOptions}
                  height={360}
                  className="w-full min-w-0"
                />
              </div>
              <div className="text-xs md:text-sm text-text-secondary">
                میانگین برند در تمام حالت‌ها نمایش داده می‌شود.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BrandOverviewBranchComparisonTable;
